import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs } from './utils'

/**
 * TODO ({ key: string; value: any }[] -> object)
 *
 * @param todo TODO
 * @example
 * TODO
 * // ->
 * TODO
 */
export interface Modulo<out T = any> {
  /** {@link Modulo} */
  readonly [Key.Modulo]: T extends number
    ? readonly [dividend: Expression<number>, divisor: Expression<number>]
    : never
}
export namespace Modulo {
  export const isModulo: TypeChecker<Modulo> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Modulo>({
      [Key.Modulo]: CommonArgs.isTwoExpressions as TypeChecker<
        Modulo[Key.Modulo]
      >,
    }),
  ).usingAlias('Fn.Modulo')
  export const assertIsModulo: TypeAsserter<Modulo> =
    TypeAsserter.Factory.from(isModulo)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.Modulo,
    {
      readonly dividend: expressionNode.Node
      readonly divisor: expressionNode.Node
    }
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type {
      return (
        [this.arg.dividend, this.arg.divisor].find(node =>
          common.value.types.Intrinsic.ForNumber.isForNumber(node.type),
        )?.type ?? common.value.types.Intrinsic.Decimal
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Modulo, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<Modulo> = {
    createNode: (fn, context) => {
      const [dividendExpression, divisorExpression] = fn[Key.Modulo]
      const dividend = expressionNode.from(dividendExpression, context)
      const divisor = expressionNode.from(divisorExpression, context)
      if (expressionNode.ForValue.isForValue(dividend)) {
        const dividendValue = Number(dividend.value)
        if (dividendValue === 0) {
          // If dividend is 0, the result is always 0 (i.e. 0 % X = 0)
          return new expressionNode.ForValue(0)
          // NOTE: 0 % 0 is handled inconsistently, since we have logic to say 0
          // % X = 0, but X % 0 = NaN. So 0 % 0 is handled differently depending
          // on if the dividend is a value node and/or the divisor is a value
          // node
        } else if (expressionNode.ForValue.isForValue(divisor)) {
          const divisorValue = Number(divisor.value)
          // If dividend and divisors are both values, we can calculate the
          // result
          return new expressionNode.ForValue(dividendValue % divisorValue)
        }
      } else if (expressionNode.ForValue.isForValue(divisor)) {
        switch (Number(divisor.value)) {
          case 0:
            // If the divisor is 0, the result is always NaN (i.e. X % 0 = NaN)
            return new expressionNode.ForValue(NaN)
          case 1:
            // If the divisor is 1, the result is always 0 (i.e. X % 1 = 0)
            return new expressionNode.ForValue(0)
        }
      }
      // TODO: if dividend and divisor are effectively the same expression, we
      // can return a value of 0 (i.e. X % X = 0)
      return new Node({ dividend, divisor })
    },
    expressionsInArgs: fn => fn[Key.Modulo],
    typeChecker: isModulo,
  }
}
